import { useFormikContext } from "formik";
import React, { FC } from "react";
import { useLocale } from "../../../contexts/localeContext";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { ListQuery } from "../../../models/ListQuery";
import { Name } from "../../../models/Name";
import { PaginationModel } from "../../../models/Pagination";
import PresetCustomField from "../../../models/PresetCustomField";
import { getPresetActionNamesWithPagination } from "../../../services/presetActionService";
import { getPresetInputNamesWithPagination } from "../../../services/presetInputService";
import { getPresetParameterNamesWithPagination } from "../../../services/presetParameterService";
import { getPresetSettingById } from "../../../services/presetService";
import { appendFilter } from "../../../utilities/filters";
import { requiredValidation } from "../../../utilities/Validation";
import Label from "../../label/label";
import PaginatedSearchableSelectField from "../../selection-field/paginated-searchable-select-field";
import { SelectType } from "../../settingFields/SettingSelectField";
import { SettingTextField } from "../../settingFields/SettingTextField";
import { toast } from "react-toastify";

interface PresetDescriptionProps {
    onNext: (event: React.MouseEvent<HTMLButtonElement>) => void;
    systemProfileId?: string;
    presetTemplateType?: SelectType;
    parentProfileId?: string;
    setCurrentItem: React.Dispatch<React.SetStateAction<PresetCustomField>>;
    setSideBarStepDescription: (newDescription : string) => void;
}

const PresetDescription: FC<PresetDescriptionProps> = ({ onNext, systemProfileId, presetTemplateType, parentProfileId, setCurrentItem, setSideBarStepDescription }) => {
    const { translate } = useLocale();
    const nameAccessor = 'name';
    const { values, errors, setFieldValue } = useFormikContext<any>();

    const getTemplateDataMethod = () => {
        switch (presetTemplateType) {
            case SelectType.presetInputTemplate:
                return (listParameter?: ListQuery) => getPresetInputNamesWithPagination({ ...listParameter, filters: appendFilter(systemProfileId, 'Profile', 'profile.id', appendFilter('I_*', 'Setting Type', 'settingType.stSysname', [])) });
            case SelectType.presetActionTemplate:
                return (listParameter?: ListQuery) => getPresetActionNamesWithPagination({ ...listParameter, filters: appendFilter(systemProfileId, 'Profile', 'profile.id', appendFilter('ACT_*', 'Setting Type', 'settingType.stSysname', [])) });
            case SelectType.presetParameterTemplate:
                return (listParameters) => getPresetParameterNamesWithPagination({ ...listParameters, filters: appendFilter(systemProfileId, 'Profile', 'profile.id', appendFilter('P_*', 'Setting Type', 'settingType.stSysname', [])) });
            default:
                return (listParameter?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => new Promise((resolve, reject) => {
                    resolve({
                        rows: new Array(0),
                        pagination: {
                            maxPageSize: 5,
                            pageNumber: 1,
                            totalCount: 0,
                            totalPages: 1,
                            sizes: [5, 50, 100],
                            pages: [1]
                        }
                    });
                })
        }
    }

    const onTemplateSelected = async (value) => {
        try {
            const preset: PresetCustomField = await getPresetSettingById(value, translate);

            if (preset) {
                setFieldValue('typeId', value)
                setFieldValue('profileId', parentProfileId);
                setFieldValue('sdist', preset.sdist);
                setFieldValue('settingIdToCopy', preset.settingIdToCopy ?? value);
                setFieldValue('typeTemplateId', preset.typeId?.toString() ?? '');
                setFieldValue('typeTemplateName', preset.type);
                setFieldValue('sdist', preset.sdist);
            }
        } catch (error) {
            toast.warn(translate.byKey(TranslationKeys.failedToLoadPresetTemplate));
        }
    }

    const onLocalNext = (e: React.MouseEvent<HTMLButtonElement>) => {
        setCurrentItem({...values});
        setSideBarStepDescription(values[nameAccessor] || "");
        onNext(e);
    }

    return <div className="flex flex-col pt-3 pr-3 h-full relative">
        <div className="flex flex-col overflow-y-auto" style={{ height: '85%' }}>
            <div className="text-portal-blue-primary-shade2 text-xl font-semibold">{translate.byKey(TranslationKeys.description)}</div>
            <div className="flex flex-row pt-2">
                <SettingTextField
                    accessor={nameAccessor}
                    required validation={(value) => requiredValidation(value, translate)}
                    autoFocus={true}
                    displayName={translate.byKey(TranslationKeys.displayName)} />
            </div>
            {!!!values.id &&
                <div className="flex flex-col flex-1 pr-6">
                    <div className="flex flex-row justify-between" id='typeLabelId'>
                        <Label
                            required
                            htmlFor='typeId'
                            className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.type)}:</Label>
                        {errors.typeId ? <span className='text-red-600 text-xs'>{errors.typeId}</span> : null}
                    </div>
                    <PaginatedSearchableSelectField
                        name={translate.byKey(TranslationKeys.type)}
                        key={'typeId'}
                        accessor={'typeId'}
                        dataMethod={getTemplateDataMethod()}
                        searchAccessor={'trnName'}
                        required={true}
                        selectedItemLabel={values.typeTemplateName}
                        onSelect={onTemplateSelected} />
                </div>}
        </div>
        <div className="flex flex-row justify-end absolute bottom-0 right-0 pr-3 pb-3 ">
            <div className="flex flex-row justify-end mt-4 pl-3">
                <button
                    className={`py-1 px-5 secondary  ${values[nameAccessor] && values.typeId ? 'secondary-enabled' : 'secondary-disabled cursor-not-allowed'}`}
                    type='button'
                    disabled={!(values[nameAccessor] && values.typeId)}
                    onClick={onLocalNext}>{translate.byKey(TranslationKeys.next)}</button>
            </div>
        </div>
    </div>
}

export default PresetDescription;